import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from 'react-query';
import { isBrowser, isMobile } from 'react-device-detect';
import { useHistory } from 'react-router-dom';
import { useUserPersonalInfo } from '../../../utilities/sharedHooks/user';
import { useLedgerFilteredCurrencies } from '../../../utilities/sharedHooks/ledgerWallets';
import {
  SETTINGS_SUPPORTED_CURRENCIES_QUERY_KEY,
  USER_PREFERENCES,
} from '../../../utilities/sharedQueries';
import { walletCurrenciesTypes } from '../../Accounts/constants';
import { isClCard } from '../../../utilities/helpers';
import { USER_DETAILS_UPDATE_KEY } from '../../../utilities/sharedMutations';
import { UPDATE_SELECTED_CURRENCY } from '../../Accounts/mutations';
import { showAlert } from '../../../utilities/notifications';
import RadioItem from '../../../components/RadioItem';
import { GenericLoading } from '../../../components/Placeholders';
import { getCurrentLanguage, switchLanguage } from '../../../i18n';

const DisplayCurrency = () => {
  const { t: translate } = useTranslation();
  const history = useHistory();

  const { currency } = useUserPersonalInfo();

  const [selectedCurrency, setSelectedCurrency] = useState(currency);
  const [selectedLang, setSelectedLang] = useState('en');

  const { data: enabledCurrencies, isLoading: isLoadingCurrencies } = useLedgerFilteredCurrencies([
    SETTINGS_SUPPORTED_CURRENCIES_QUERY_KEY,
    { currency_type: walletCurrenciesTypes.FIAT },
  ]);

  const isLedgerFlavor = !process.env.REACT_APP_FLAVOR || process.env.REACT_APP_FLAVOR === 'ledger';

  const availableLangs = useMemo(
    () => [
      {
        value: 'en',
        label: 'lang_name_en',
      },
      {
        value: 'de',
        label: 'lang_name_de',
      },
    ],
    []
  );

  useEffect(() => {
    const currentLang = getCurrentLanguage();
    setSelectedLang(currentLang);
  }, []);

  const allCurrencies = useMemo(() => {
    if (!enabledCurrencies) return [];
    const currencies = [];
    // eslint-disable-next-line array-callback-return
    Object.keys(enabledCurrencies).map((c) => {
      if (
        isClCard(enabledCurrencies[c].currency_display_name.toLowerCase()) ||
        !enabledCurrencies[c].allow_as_display_currency
      ) {
        return;
      }
      currencies.push({
        label: enabledCurrencies[c].currency_display_name,
        value: enabledCurrencies[c].currency_display_name,
      });
    });
    return currencies;
  }, [enabledCurrencies]);

  const queryClient = useQueryClient();

  const applyUserPreferences = (userData) => {
    // eslint-disable-next-line no-shadow
    const { currency } = userData.user;
    queryClient.setQueryData(USER_PREFERENCES, () => ({
      displayCurrency: currency.toUpperCase(),
    }));
  };

  const { mutate: refreshUserDetails } = useMutation(USER_DETAILS_UPDATE_KEY, {
    onSettled: (userData, error) => {
      if (!error) {
        applyUserPreferences(userData);
      }
    },
  });

  const { mutate: updateSelectedCurrency, isLoading: isLoadingUpdateSelectedCurrency } =
    useMutation(UPDATE_SELECTED_CURRENCY, {
      onSettled(_, error) {
        if (error) {
          showAlert({ message: error.message, type: 'danger' });
        } else {
          refreshUserDetails();
          if (isMobile) {
            history.push('/dashboard/settings');
          } else {
            history.push('/dashboard/settings/language-currency');
          }
          showAlert({
            message: translate('update_selected_currency_success'),
            type: 'success',
          });
        }
      },
    });

  if (isLoadingCurrencies) {
    return <GenericLoading isFullScreen={false} />;
  }

  return (
    <div className="my-3 d-flex flex-column w-100 h-lg-100 h-md-100 h-sm-100 h-100">
      <p className="text-f3 text-dark pe-5 text-bold letter-spacing-1 line-height-sm-1 line-height-2">
        {translate('language_display_currency_label')}
      </p>
      <div className="mt-3 d-flex flex-column gap-5">
        {isLedgerFlavor && (
          <div>
            <p className="text-f6 text-shadow letter-spacing-3">
              {translate('app_language_label')}
            </p>
            <div className="d-flex flex-column justify-content-between h-100">
              <div className="d-flex flex-column mt-4 bg-sm-lighter rounded-sm-extra px-sm-3 px-0">
                {/* eslint-disable-next-line no-shadow */}
                {availableLangs.map((lang, index) => (
                  <>
                    <RadioItem
                      key={lang.value}
                      backgroundColor="bg-lighter"
                      className="border-sm-0 border-1"
                      paddingSpacing="py-4 px-sm-0 px-3"
                      marginSpacing={isBrowser ? 'my-0' : 'my-1'}
                      label={translate(lang.label)}
                      checked={selectedLang === lang.value}
                      name="language"
                      onChange={() => {
                        setSelectedLang(lang.value);
                        if (isBrowser) {
                          switchLanguage(lang.value);
                        }
                      }}
                    />
                    {isBrowser && index !== availableLangs.length - 1 && (
                      <hr className="m-0 text-black text-f5" style={{ paddingTop: '1px' }} />
                    )}
                  </>
                ))}
              </div>
            </div>
          </div>
        )}

        <div>
          <p className="mt-3 text-f6 text-shadow letter-spacing-3">
            {translate('display_currency_label')}
          </p>
          <div className="d-flex flex-column justify-content-between h-100">
            <div className="d-flex flex-column mt-4 bg-sm-lighter rounded-sm-extra px-sm-3 px-0">
              {/* eslint-disable-next-line no-shadow */}
              {allCurrencies?.map((currency, index) => (
                <>
                  <RadioItem
                    key={currency.value}
                    backgroundColor="bg-lighter"
                    className="border-sm-0 border-1"
                    paddingSpacing="py-4 px-sm-0 px-3"
                    marginSpacing={isBrowser ? 'my-0' : 'my-1'}
                    coin={currency.label}
                    label={currency.label}
                    checked={selectedCurrency === currency.value}
                    name="displayCurrency"
                    onChange={() => {
                      setSelectedCurrency(currency.value);
                      if (isBrowser) {
                        updateSelectedCurrency({ currency: currency.value });
                      }
                    }}
                  />
                  {isBrowser && index !== allCurrencies.length - 1 && (
                    <hr className="m-0 text-black text-f5" style={{ paddingTop: '1px' }} />
                  )}
                </>
              ))}
            </div>
          </div>
        </div>
        <div className="d-sm-none d-flex flex-column justify-content-end align-items-center">
          <button
            type="button"
            className="btn py-3 btn-primary w-100"
            loading={isLoadingUpdateSelectedCurrency ? 'true' : 'false'}
            onClick={() => {
              if (isLedgerFlavor) {
                switchLanguage(selectedLang);
              }

              updateSelectedCurrency({ currency: selectedCurrency });
            }}>
            {translate('confirm_button_label')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default DisplayCurrency;
